import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Polygon } from "../elements/Polygon";
import MapPropertiesService from '../../services/MapPropertiesService';
import {
    useSelectedCoastalSegmentGroup,
    useSetSelectedCoastalSegmentGroup,
    useSetMapTitle,
    useFeatureMouseOver,
} from '../../hooks/useMapStore';
import { useDarkmode } from 'components/hooks/useAppStore';
import { isMobile } from 'components/helpers/DeviceHelpers';

interface PilotCertificateGroupAreaFeaturePolygonProps {
    source: any
}
export const PilotCertificateGroupAreaFeaturePolygon = ({
    source
}: PilotCertificateGroupAreaFeaturePolygonProps) => {

    const defaultOpacity = 0.3;
    const hoverOpacity = 0.6;

    const darkmode = useDarkmode();

    const selectedCoastalSegmentGroup = useSelectedCoastalSegmentGroup();
    const setSelectedCoastalSegmentGroup = useSetSelectedCoastalSegmentGroup();
    const setMapTitle = useSetMapTitle();

    const featureMouseOver = useFeatureMouseOver();

    const [isSelected, setIsSelected] = useState(source.properties.isSelected);
    const [fillOpacity, setFillOpacity] = useState(defaultOpacity);
    const [hovered, setHovered] = useState(false);

    const mapProperties = MapPropertiesService.properties;

    const fillColor = useMemo(
        () => {
            const fill = source.properties.status === "APPROVED" ?
                mapProperties.ColorCertificateApproved:
                mapProperties.ColorCertificateReadyForApproval;

            return hovered ?
                "#007D97"
                :
                isSelected ?
                    fill
                    : mapProperties.ColorCertificateAvailable;
        },
        [isSelected, mapProperties, hovered]
    );
    const strokeColor = useMemo(
        () => {
            return darkmode ?
                mapProperties.ColorCertificateFairwayAreaStrokeDark :
                mapProperties.ColorCertificateFairwayAreaStroke
        },
        [darkmode, mapProperties]
    );

    const handleSelect = useCallback(() => {
        setSelectedCoastalSegmentGroup({
            id: source.id,
            name: source.properties.segmentName,
            isSelected: !isSelected,
            status: source.properties.status
        });
    }, [isSelected, source, setSelectedCoastalSegmentGroup]);

    const onMouseOver = useCallback(() => {
        if (isMobile()) return;
        setFillOpacity(hoverOpacity);
        setMapTitle(source.properties?.segmentName ?? source.properties?.name ?? "");
    }, [setMapTitle, source.properties]);

    const onMouseOut = useCallback(() => {
        if (isMobile()) return;
        setFillOpacity(defaultOpacity);
        setMapTitle("");
    }, [setMapTitle]);

    useEffect(() => {
        if(!featureMouseOver) {
            setHovered(false);
            return;
        }
        setHovered(featureMouseOver.id === source.id);
    }, [featureMouseOver]);

    useEffect(() => {
        if(!selectedCoastalSegmentGroup || selectedCoastalSegmentGroup?.id !== source.id) return;
        setIsSelected(selectedCoastalSegmentGroup.isSelected);
    }, [selectedCoastalSegmentGroup]);

    return (
        <Polygon
            paths={source.paths}
            fillColor={fillColor}
            fillOpacity={fillOpacity}
            strokeColor={strokeColor}
            strokeWeight={2}
            strokeOpacity={0.8}
            zIndex={1}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            onClick={handleSelect}
        />
    );
}
